/**
 * Product support routing for REChecklist.pro / DreamHomeJourney.online.
 *
 * Mailbox status (this prototype cannot verify live MX/inboxes):
 * - marketing@rechecklist.pro — already used in product contact copy
 * - sysadmin@, support@, recprosupport@, billing@, payments@, admin@ — intended
 *   routing table below; delivery is mailto + a local queue until a mail server
 *   is connected. Do not treat these as confirmed live mailboxes.
 */

export const SUPPORT_INBOXES = {
  sysadmin: {
    id: "sysadmin",
    email: "sysadmin@rechecklist.pro",
    label: "System questions",
    audience: "all",
    allowTechnical: true,
  },
  support: {
    id: "support",
    email: "support@rechecklist.pro",
    label: "General help",
    audience: "all",
    allowTechnical: false,
  },
  recprosupport: {
    id: "recprosupport",
    email: "recprosupport@rechecklist.pro",
    label: "Using REChecklist",
    audience: "all",
    allowTechnical: false,
  },
  billing: {
    id: "billing",
    email: "billing@rechecklist.pro",
    label: "Billing",
    audience: "billing",
    allowTechnical: false,
  },
  payments: {
    id: "payments",
    email: "payments@rechecklist.pro",
    label: "Payments",
    audience: "billing",
    allowTechnical: false,
  },
  admin: {
    id: "admin",
    email: "admin@rechecklist.pro",
    label: "Office and accounts",
    audience: "office",
    allowTechnical: false,
  },
};

/** Human topic list — no tech jargon in the labels. */
export const SUPPORT_TOPICS = [
  {
    id: "beta",
    label: "I was invited to the Beta Tester Program",
    inbox: "sysadmin",
  },
  {
    id: "help",
    label: "I would like a hand using REChecklist",
    inbox: "recprosupport",
  },
  {
    id: "stuck",
    label: "I feel stuck, or something is not working",
    inbox: "sysadmin",
  },
  {
    id: "billing",
    label: "A question about billing",
    inbox: "billing",
  },
  {
    id: "payment",
    label: "A question about a payment",
    inbox: "payments",
  },
  {
    id: "account",
    label: "My office or my account",
    inbox: "admin",
  },
  {
    id: "general",
    label: "A general note",
    inbox: "support",
  },
];

export function topicById(topicId) {
  return SUPPORT_TOPICS.find((item) => item.id === topicId) || SUPPORT_TOPICS[SUPPORT_TOPICS.length - 1];
}

export function inboxForTopic(topicId) {
  const topic = topicById(topicId);
  return SUPPORT_INBOXES[topic.inbox] || SUPPORT_INBOXES.support;
}

export function supportMailto(inbox, { name = "", email = "", message = "", technical = "" } = {}) {
  const to = inbox?.email || SUPPORT_INBOXES.support.email;
  const subject = encodeURIComponent(`${inbox?.label || "Support"} — ${name || "REChecklist"}`.trim());
  const lines = [
    message.trim(),
    technical.trim() ? `\n---\nAdditional detail:\n${technical.trim()}` : "",
    name || email ? `\n— ${[name, email].filter(Boolean).join(" · ")}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:${to}?subject=${subject}&body=${encodeURIComponent(lines)}`;
}

export const SUPPORT_MAIL_KEY = "dh_j_support_mail";

export function readSupportQueue() {
  try {
    return JSON.parse(localStorage.getItem(SUPPORT_MAIL_KEY) || "[]");
  } catch {
    return [];
  }
}

export function queueSupportNote(entry) {
  const inbox = inboxForTopic(entry.topic);
  const row = {
    id: crypto.randomUUID(),
    at: new Date().toISOString(),
    topic: entry.topic,
    inbox: inbox.id,
    to: inbox.email,
    name: String(entry.name || "").trim(),
    email: String(entry.email || "").trim(),
    message: String(entry.message || "").trim(),
    technical: inbox.allowTechnical ? String(entry.technical || "").trim() : "",
    status: "queued",
  };
  const rows = [...readSupportQueue(), row];
  localStorage.setItem(SUPPORT_MAIL_KEY, JSON.stringify(rows));
  return { row, inbox, mailto: supportMailto(inbox, row) };
}
